import { useEffect, useState } from 'react';
import { listQueueItems, syncNow, retryItem, discardItem } from '../lib/sync';
import type { QueueItem } from '../lib/offline';
import { useToast } from '../context/ToastContext';

const KIND_LABELS: Record<string, string> = {
  reception: 'Réception', issue: 'Remise', loss: 'Perte', wash_batch: 'Lot de lavage', autre: 'Action',
};

export function Sync() {
  const toast = useToast();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setItems(await listQueueItems());
  }

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, []);

  async function sync() {
    setBusy(true);
    try {
      const { synced, failed } = await syncNow();
      await refresh();
      if (!navigator.onLine) toast('Toujours hors ligne — réessayez une fois le réseau revenu.', true);
      else if (synced || failed) toast(`${synced} synchronisée(s)${failed ? `, ${failed} en erreur` : ''}`);
      else toast('Rien à synchroniser');
    } finally {
      setBusy(false);
    }
  }

  async function retry(id: string) {
    await retryItem(id);
    refresh();
  }

  async function discard(id: string) {
    if (!confirm("Abandonner définitivement cette action ? Elle ne sera jamais envoyée au serveur.")) return;
    await discardItem(id);
    refresh();
    toast('Action abandonnée');
  }

  const pending = items.filter((i) => i.status === 'pending');
  const errors = items.filter((i) => i.status === 'error');

  return (
    <>
      <div className="pagehead">
        <h1>Synchronisation</h1>
        <button className="primary" onClick={sync} disabled={busy}>Synchroniser maintenant</button>
      </div>
      <div className="card">
        <h2>En attente ({pending.length})</h2>
        <p className="muted">Saisies faites hors connexion (réceptions, remises, pertes, lots de lavage) : envoyées automatiquement dès le retour du réseau, sans jamais créer de doublon.</p>
        {!pending.length && <p className="muted">Aucune action en attente.</p>}
        {pending.length > 0 && (
          <div className="tblwrap"><table><tbody>
            <tr><th>Type</th><th>Détail</th><th>Saisi le</th></tr>
            {pending.map((i) => (
              <tr key={i.id}>
                <td>{KIND_LABELS[i.kind] || i.kind}</td>
                <td>{i.label}</td>
                <td>{new Date(i.createdAt).toLocaleString('fr-FR')}</td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </div>
      {errors.length > 0 && (
        <div className="card">
          <h2>En erreur ({errors.length})</h2>
          <p className="muted">Le serveur a refusé ces actions à la resynchronisation (incohérence détectée, ex. stock devenu insuffisant) — elles n'ont PAS été appliquées. Vérifiez puis réessayez ou abandonnez.</p>
          <div className="tblwrap"><table><tbody>
            <tr><th>Type</th><th>Détail</th><th>Erreur</th><th></th></tr>
            {errors.map((i) => (
              <tr key={i.id}>
                <td>{KIND_LABELS[i.kind] || i.kind}</td>
                <td>{i.label}</td>
                <td className="err">{i.lastError}</td>
                <td className="row">
                  <button className="small" onClick={() => retry(i.id)}>Réessayer</button>
                  <button className="small danger-btn" onClick={() => discard(i.id)}>Abandonner</button>
                </td>
              </tr>
            ))}
          </tbody></table></div>
        </div>
      )}
    </>
  );
}
